import { useTimeline } from "@/hooks/useTimeline";
import { timelineEvents } from "@/data/timeline";

export default function TimelineSection({ timelineTitle = "Línea de tiempo" }: { timelineTitle?: string }) {
  const { data: events } = useTimeline(timelineEvents);

  return (
    <section className="py-14 md:py-18 lg:py-24 bg-masmed-gray-50">
      <div className="max-w-[1180px] mx-auto px-4 md:px-8 lg:px-12">
        {/* Título centrado */}
        <div className="text-center mb-3 md:mb-4">
          <h2 className="text-2xl md:text-[28px] text-masmed-gray-600 tracking-tight">
            {timelineTitle}
          </h2>
        </div>

        {/* Línea ondulada decorativa naranja */}
        <div className="flex justify-center mb-10 md:mb-14 lg:mb-16">
          <svg viewBox="0 0 320 12" className="w-48 md:w-64 h-4" preserveAspectRatio="none">
            <path
              d="M0,6 Q16,0 32,6 T64,6 T96,6 T128,6 T160,6 T192,6 T224,6 T256,6 T288,6 T320,6"
              stroke="#E45B2D"
              strokeWidth="2.5"
              fill="none"
              strokeLinecap="round"
            />
          </svg>
        </div>

        {/* Timeline horizontal */}
        <div className="relative">
          {/* Línea de conexión horizontal */}
          <div className="absolute left-0 right-0 top-[90px] md:top-[102px] lg:top-[108px] h-[2px] bg-masmed-gray-400" />

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-x-2 md:gap-x-4 lg:gap-x-5">
            {events.map((event) => {
              const isFilled = event.orden <= 2;
              const numStr = String(event.orden).padStart(2, "0");
              // Usar estado_tipo y estado_color desde la BD
              const hasEstado = !!event.estado_tipo;
              const estadoColor = event.estado_color || "#1A7A7A";

              return (
                <div key={event.id} className="flex flex-col items-center text-center relative">
                  {/* Título arriba */}
                  <div className="mb-3 md:mb-4 h-[60px] md:h-[72px] flex items-end justify-center w-full px-1">
                    <p className="text-masmed-gray-900 text-sm md:text-base leading-snug font-medium text-center">
                      {event.titulo}
                    </p>
                  </div>

                  {/* Círculo con indicador de estado */}
                  <div className="relative flex items-center justify-center">
                    <div
                      className={`
                        w-12 h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-full
                        flex items-center justify-center
                        text-base md:text-lg lg:text-xl
                        transition-colors duration-200
                        ${isFilled
                          ? "bg-primary text-white"
                          : "bg-white text-primary border-2 border-masmed-gray-400"
                        }
                      `}
                      style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 700 }}
                    >
                      {numStr}
                    </div>

                    {/* Indicador de estado sobrepuesto (abajo del círculo) — desde la BD */}
                    {hasEstado && (
                      <div
                        className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-5 h-5 md:w-6 md:h-6 rounded-full flex items-center justify-center z-10"
                        style={{ backgroundColor: estadoColor }}
                      >
                        {event.estado_tipo === "check" && (
                          <i className="ri-check-line text-white text-xs"></i>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Descripción abajo */}
                  <div className="mt-5 md:mt-6 w-full px-1">
                    <p className="text-masmed-gray-500 text-xs md:text-sm leading-relaxed text-center whitespace-pre-line">
                      {event.descripcion}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}